import { useMemo } from "react";

import { useFormikWithDirtyForm } from "@shared";
import { useAlertContext } from "@shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { CourtResponse, courtsApi } from "@/api";
import { withLocalErrorHandling } from "@/api/middleware/errorMiddleware";
import { QueryKey } from "@/query/Query";
import { useTranslation } from "@/translations";

import { CourtFormType } from "../components/court-dialog/court-form-fields/CourtForm.types";
import {
  applyCourtSaveError,
  courtToFormValues,
  courtValidationSchema,
  formValuesToRequest,
} from "../components/court-dialog/court-form-fields/CourtForm.utils";
import { useCourtDelete } from "./useCourtDelete";

export function useCourtEdit(court: CourtResponse, onClose: () => void) {
  const { t } = useTranslation();
  const { showSuccess, showError } = useAlertContext();
  const queryClient = useQueryClient();
  const { confirmAndDelete, isDeleting } = useCourtDelete(onClose);

  const updateMutation = useMutation({
    mutationFn: (values: CourtFormType) =>
      courtsApi.updateCourt({ id: court.id, updateCourtRequest: formValuesToRequest(values) }, withLocalErrorHandling),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [QueryKey.Courts] });
      void queryClient.invalidateQueries({ queryKey: [QueryKey.AvailableCourtDistricts] });
      showSuccess(t("settings.courts.successfullyUpdated"));
      onClose();
    },
  });

  const validationSchema = useMemo(() => courtValidationSchema(t), [t]);

  const formik = useFormikWithDirtyForm<CourtFormType>({
    initialValues: courtToFormValues(court),
    enableReinitialize: true,
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    onSubmit: async (values, { setFieldError }) => {
      try {
        await updateMutation.mutateAsync(values);
      } catch (error) {
        await applyCourtSaveError(error, setFieldError, showError, t);
      }
    },
  });

  const handleDelete = () => confirmAndDelete(court);

  return { formik, handleDelete, isDeleting };
}
